import { defineStore } from 'pinia'
import { ref } from 'vue'
import { Auth } from 'aws-amplify'
import { useStorage } from '@vueuse/core'
import router from '../router'

import { Notify } from 'quasar'
export const useAuthStore = defineStore('auth', () => {
  const isLoading = ref(false)

  const email = useStorage('email', '')
  const tokenBackoffice = useStorage('tokenBackoffice', '')
  const codeSent = ref(false)
  const userProfile = useStorage('userProfile', [])
  const userDept = useStorage('userDept', null)

  const cognitoUser = ref(null)

  const isAuthenticated = () => !!tokenBackoffice.value

  const setProfile = (profile) => {
    userProfile.value = profile
  }

  const login = async (accessEmail: string, password: string) => {
    isLoading.value = true
    await Auth.signIn(accessEmail, password)
      .then(async (user) => {
        const challenge = user.challengeName
        if (challenge === 'NEW_PASSWORD_REQUIRED') {
          email.value = accessEmail
          cognitoUser.value = user
          router.push('/temporary-password')
        } else {
          userDept.value = user.attributes['custom:company'] ?? null
          tokenBackoffice.value = user.signInUserSession.accessToken.jwtToken
          email.value = user.attributes.email
          router.push('/home')
        }
      })
      .catch((err) => {
        Notify.create({
          message: err.message,
          color: 'negative',
          icon: 'warning',
        })
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const clearSession = () => {
    tokenBackoffice.value = ''
    email.value = ''
    userDept.value = null
    userProfile.value = []
  }

  const logout = () => {
    clearSession()
    Auth.signOut().then(() => {
      router.push('/login')
    })
  }

  const sendResetPasswordCode = async (accessEmail: string) => {
    try {
      isLoading.value = true
      await Auth.forgotPassword(accessEmail)
      codeSent.value = true
      Notify.create({
        message: `Código enviado para o email ${accessEmail}.`,
        color: 'positive',
        icon: 'check',
      })
    } catch (error) {
      Notify.create({
        message:
          error.code === 'UserNotFoundException'
            ? 'Usuário não encontrado.'
            : error.code === 'NotAuthorizedException'
            ? 'Usuário inativo.'
            : error.message,
        color: 'negative',
        icon: 'warning',
      })
    } finally {
      isLoading.value = false
    }
  }

  const resetPassword = async (accessEmail: string, code: string, password: string) => {
    isLoading.value = true
    try {
      await Auth.forgotPasswordSubmit(accessEmail, code, password)
      Notify.create({
        message: 'Senha alterada com sucesso.',
        color: 'positive',
        icon: 'check',
      })
      router.push('/login')
    } catch (error) {
      Notify.create({
        message: error.message,
        color: 'negative',
        icon: 'warning',
      })
    } finally {
      isLoading.value = false
    }
  }

  const resetTemporaryPassword = async (password: string) => {
    isLoading.value = true
    try {
      const user = await Auth.completeNewPassword(cognitoUser.value, password)
      const token = user.signInUserSession.accessToken.jwtToken
      tokenBackoffice.value = token
      userDept.value = user?.challengeParam?.userAttributes['custom:company'] || null
    } catch (err) {
      Notify.create({
        message: err.message,
        color: 'negative',
        icon: 'warning',
      })
    } finally {
      isLoading.value = false
    }
  }

  return {
    email,
    userDept,
    userProfile,
    setProfile,
    tokenBackoffice,
    isAuthenticated,
    login,
    logout,
    clearSession,
    isLoading,
    sendResetPasswordCode,
    resetPassword,
    codeSent,
    resetTemporaryPassword,
  }
})
